import { useMemo, useState } from 'react';
import { MotivoCreditoDialog } from '../cotacao/MotivoCreditoDialog';
import { Zap, Check, Clock, DollarSign, X, AlertTriangle, CalendarClock, ShieldX } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ehDoSegmento, rotuloSegmento, useSegmentoTriagem } from '@/components/kanban/triagem/segmentoTriagem';
import { TriagemShell } from '@/components/kanban/triagem/TriagemShell';
import { TRIAGEM_TH } from '@/components/kanban/triagem/triagemTabela';
import { useLinhasOcupadas } from '@/components/kanban/triagem/acaoTriagem';
import { agruparPorPedido, useTriagemSolar } from './useTriagemSolar';
import { TriagemSolarRow } from './TriagemSolarRow';
import { separarAparelho, useFlagsFaturamento } from '@/components/kanban/triagem/flagsFaturamento';

interface TriagemSolarModalProps {
  isOpen: boolean;
  onClose: () => void;
  familia?: 'MOVEL' | 'FIXA';
}

export function TriagemSolarModal({ isOpen, onClose, familia }: TriagemSolarModalProps) {
  const [creditoItemId, setCreditoItemId] = useState<string | null>(null);
  const {
    itens: itensTodos,
    isLoading,
    refetch,
    moverParaErro,
    moverParaFinal,
    confirmarCotacao,
    moverParaCreditoNegado,
    moverParaCreditoReprovadoAsync,
    entrarEmInstalacao,
    podeEmInstalacao,
    isMovingToErro,
    isMovingToFinal,
    isConfirmingCotacao,
    isMovingToCreditoNegado,
    isMovingToCreditoReprovado,
    isEntrandoInstalacao,
  } = useTriagemSolar(familia);
  /* Só o pedido clicado fica ocupado; o resto da fila continua livre
     (`triagem/acaoTriagem.ts`). A linha da fila é o pedido nesta coluna. */
  const { ocupada, ocupar } = useLinhasOcupadas();
  const chaveLinha = (p: { pedido_id: string; coluna_atual: string }) => `${p.pedido_id}|${p.coluna_atual}`;
  const naLinha = (chave: string, fn: () => Promise<unknown>) => { void ocupar(chave, fn).catch(() => undefined); };

  // Sem número de cotação a defesa não pode ser registrada depois — a triagem de
  // Crédito Reprovado trata por cotação. O número é pedido aqui, na entrada.
  const itemCredito = itensTodos.find(i => i.item_id === creditoItemId);

  /* ⛔ CNPJ e CPF NÃO SE MISTURAM NA FILA (Douglas 17/09/2026). A régua de
     entrada desta triagem é a MESMA de sempre — o que muda é a fila mostrar um
     segmento por vez, e os contadores do topo falarem do que está na tela.
     A separação é filtro, nunca coluna nova: nada troca de fila fora da
     triagem. Lei em `triagem/segmentoTriagem.ts`. */
  const { segmento, setSegmento } = useSegmentoTriagem();
  const itens = useMemo(
    () => itensTodos.filter((r) => ehDoSegmento(r.tipo_pessoa, segmento)),
    [itensTodos, segmento],
  );

  const pedidos = useMemo(() => agruparPorPedido(itens), [itens]);
  const isFixa = familia === 'FIXA';
  const uniquePedidos = new Set(itens.map(i => i.pedido_id)).size;
  // Aparelho anda SEPARADO do faturamento — tem KPI próprio e não é venda de
  // linha. Régua única em `flagsFaturamento.ts`.
  const flagsFat = useFlagsFaturamento();
  const { faturamento: totalValue, qtdAparelhos } = separarAparelho(
    itens, flagsFat, (i) => i.produto, (i) => (i.subtotal || 0),
  );

  return (
    <>
    <TriagemShell
      isOpen={isOpen}
      onFechar={onClose}
      Icone={Zap}
      titulo={`Triagem Solar${familia ? ` - ${familia === 'MOVEL' ? 'Móvel' : 'Fixa'}` : ''}`}
      subtitulo={isFixa ? 'consulta da fibra · marque a data e o turno e mande para Em instalação' : 'fila do quadro Tramitação Vivo · prazo 5h úteis'}
      contadores={{ pedidos: uniquePedidos, itens: itens.length, valor: totalValue, aparelhos: qtdAparelhos }}
      segmento={segmento}
      onSegmento={setSegmento}
      consulta={familia ? { chave: familia === 'MOVEL' ? 'input_movel' : 'input_fixa', pedidoIds: itens.map(i => i.pedido_id) } : undefined}
      sla={null}
      fifo={false}
      legenda={[
        { key: 'manter', Icone: Clock, label: 'Manter' },
        ...(familia === 'FIXA'
          ? [
              { key: 'em-instalacao', Icone: CalendarClock, label: 'Em instalação (data e turno)' },
              { key: 'pedido-cancelado', Icone: X, label: 'Pedido Cancelado' },
            ]
          : [
              { key: 'cota-o-ok', Icone: Check, label: 'Cotação OK' },
              { key: 'erro', Icone: AlertTriangle, label: 'Erro (input manual)' },
              { key: 'tt-recursos', Icone: DollarSign, label: 'TT Recursos' },
              { key: 'cr-dito-reprovado-solar', Icone: ShieldX, label: 'Crédito Reprovado Solar' },
            ]),
      ]}
      isLoading={isLoading}
      onAtualizar={async () => { await refetch(); toast({ title: 'Dados atualizados', duration: 1500 }); }}
      vazio={
        itens.length === 0
          ? { titulo: 'Nenhum pedido pendente', descricao: 'Todos os pedidos foram processados no Solar' }
          : undefined
      }
    >
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <thead className="sticky top-0 z-10 bg-surface">
                <tr>
                  <th className={TRIAGEM_TH}>{rotuloSegmento(segmento)}</th>
                  <th className={TRIAGEM_TH}>Razão Social</th>
                  {!isFixa && <th className={`${TRIAGEM_TH} w-[96px]`}>Cotação</th>}
                  <th className={`${TRIAGEM_TH} w-[96px]`}>Pedido</th>
                  <th className={TRIAGEM_TH}>Produto</th>
                  {!isFixa && <th className={`${TRIAGEM_TH} w-[44px]`}>Qtd</th>}
                  {!isFixa && <th className={`${TRIAGEM_TH} w-[92px]`}>Valor</th>}
                  {isFixa && <th className={`${TRIAGEM_TH} w-[110px]`}>Data Instalação</th>}
                  <th className={`${TRIAGEM_TH} w-[128px]`}>Tempo</th>
                  <th className={`${TRIAGEM_TH} w-[206px]`}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {pedidos.map((pedido) => {
                  const chave = chaveLinha(pedido);
                  const oc = ocupada(chave);
                  return (
                  <TriagemSolarRow
                    key={`${pedido.pedido_id}-${pedido.coluna_atual}`}
                    pedido={pedido}
                    familia={familia}
                    onMoverParaErro={(itemId) => naLinha(chave, () => moverParaErro(itemId))}
                    onMoverParaFinal={(itemId) => naLinha(chave, () => moverParaFinal(itemId))}
                    onConfirmarCotacao={(itemId, numeroCotacao) =>
                      naLinha(chave, () => confirmarCotacao({ itemId, numeroCotacao }))
                    }
                    onMoverParaCreditoNegado={(itemId) => naLinha(chave, () => moverParaCreditoNegado(itemId))}
                    onMoverParaCreditoReprovado={setCreditoItemId}
                    onEntrarEmInstalacao={(itemId, dataIso) => naLinha(chave, () => entrarEmInstalacao({ itemId, dataIso }))}
                    podeEmInstalacao={podeEmInstalacao(pedido.tipo_pessoa)}
                    ocupada={oc}
                    isMovingToErro={oc && isMovingToErro}
                    isMovingToFinal={oc && isMovingToFinal}
                    isConfirmingCotacao={oc && isConfirmingCotacao}
                    isMovingToCreditoNegado={oc && isMovingToCreditoNegado}
                    isMovingToCreditoReprovado={oc && isMovingToCreditoReprovado}
                    isEntrandoInstalacao={oc && isEntrandoInstalacao}
                  />
                  );
                })}
              </tbody>
            </table>
    </TriagemShell>
    <MotivoCreditoDialog open={!!creditoItemId} onClose={() => setCreditoItemId(null)}
      isLoading={isMovingToCreditoReprovado}
      cotacaoInicial={itemCredito && !itemCredito.numero_cotacao_operadora?.trim() ? '' : undefined}
      onConfirm={({ motivo, cotacao }) => {
        const pedir = () => moverParaCreditoReprovadoAsync({ itemId: creditoItemId!, motivo, cotacao });
        return itemCredito ? ocupar(chaveLinha(itemCredito), pedir) : pedir();
      }} />
    </>
  );
}
